import { AnimatePresence, motion } from 'framer-motion';
import { IconButton, useTranslationContext } from 'platform/components';
import { match } from 'ts-pattern';
import type { ReactElement } from 'react';
import { useIsMobileView } from '../../hooks/useIsMobileView';
import { usePresetState } from '../../hooks/usePresetState';
import { useColumnStates } from '../../store/selectors/useColumnStates';
import type { FeGridSettings } from '../../types/Api';
import type { EagColumn } from '../../types/EagColumn';
import type { EagColumnOrGroup } from '../../types/EagColumnGroup';
import type { FilterState } from '../../types/FilterState';
import type { FormToDataGridConnectorProps } from '../../types/FormToDataGridConnectorProps';
import { ToolpanelType } from '../../types/ToolpanelType';
import { getPresetStates } from '../../utils/getPresetStates';
import { ColumnsPanel } from './components/ColumnsPanel/ColumnsPanel';
import { CustomFiltersPanel } from './components/CustomFiltersPanel/CustomFiltersPanel';
import { FilterPanel } from './components/FilterPanel/FilterPanel';
import { MobileFilterPanel } from './components/MobileFilterPanel/MobileFilterPanel';
import { MobilePresetsDialog } from './components/MobilePresetsDialog/MobilePresetsDialog';
import { SettingsPanel } from './components/SettingsPanel/SettingsPanel';
export type SidebarProps = {
  columns: EagColumn[];
  columnConfig: EagColumnOrGroup[];
  filterState: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
  onResetFilters: () => void;
  settings: FeGridSettings;
  onSettingsChange: (settings: Partial<FeGridSettings>) => void;
  filterComponent?: (props: FormToDataGridConnectorProps) => ReactElement;
  activePanel: ToolpanelType | null;
  onClose: () => void;
};
const SLIDE_PANEL_DEFAULT_WIDTH = 320;
const SLIDE_PANEL_FILTER_WIDTH = 'clamp(0px, 75vw, 700px)';
const SLIDE_PANEL_COLUMN_WIDTH = 'clamp(320px, 30vw, 500px)';
const SLIDE_ANIMATION = {
  initial: {
    x: '100%'
  },
  animate: {
    x: 0
  },
  exit: {
    x: '100%'
  },
  transition: {
    duration: 0.2,
    ease: 'easeInOut'
  }
};
export function Sidebar(props: SidebarProps) {
  const t = useTranslationContext();
  const isMobile = useIsMobileView();
  const presetState = usePresetState();
  const columnStates = useColumnStates();
  const currentColumnSettings = getPresetStates(columnStates);
  const createPresetAdapter = (name: string, desc: string) => presetState.createPreset(name, desc, currentColumnSettings, props.settings);
  const updatePresetAdapter = (presetId: string) => presetState.updatePreset(presetId, currentColumnSettings, props.settings);
  const sidebarWidth = match({
    isMobile,
    panel: props.activePanel
  }).with({
    isMobile: true
  }, () => '100%' as const).with({
    panel: ToolpanelType.filter
  }, () => SLIDE_PANEL_FILTER_WIDTH).with({
    panel: ToolpanelType.column
  }, () => SLIDE_PANEL_COLUMN_WIDTH).otherwise(() => SLIDE_PANEL_DEFAULT_WIDTH);
  const panelContent = match(props.activePanel).with(ToolpanelType.column, () => <ColumnsPanel columns={props.columns} columnConfig={props.columnConfig} />).with(ToolpanelType.filter, () => {
    if (props.filterComponent) {
      return <CustomFiltersPanel filterComponent={props.filterComponent} />;
    }
    // Mobile filters rendered via MobileFilterPanel portal below
    if (isMobile) {
      return null;
    }
    return <FilterPanel columnConfig={props.columnConfig} filterState={props.filterState} onFilterChange={props.onFilterChange} onResetFilters={props.onResetFilters} />;
  }).with(ToolpanelType.settings, () => <SettingsPanel settings={props.settings} onSettingsChange={props.onSettingsChange} />).with(ToolpanelType.presets, () => <MobilePresetsDialog presets={presetState.presets} activePreset={presetState.activePreset} isPresetChanged={presetState.isPresetChanged} onActivate={presetState.activatePreset} onDeactivate={presetState.deactivatePreset} onCreate={createPresetAdapter} onUpdate={updatePresetAdapter} onDelete={presetState.deletePreset} onDuplicate={presetState.duplicatePreset} onRename={presetState.renamePreset} onResetOverrides={() => presetState.setIsPresetChanged(false)} onUpdatePosition={(_presetId, from, to) => presetState.updatePresetPosition(from, to)} />).with(null, () => null).exhaustive();
  const panelTitle = match(props.activePanel).with(ToolpanelType.filter, () => t('page.datagrid.labels.filters')).with(ToolpanelType.column, () => t('page.datagrid.labels.columnSetting')).with(ToolpanelType.settings, () => t('page.datagrid.labels.settings')).with(ToolpanelType.presets, () => t('page.datagrid.labels.presets')).with(null, () => '').exhaustive();

  // Mobile filter is rendered as a portal (MobileFilterPanel) — the slide panel
  // must not open for this case to avoid a ghost empty panel.
  const showSlidePanel = props.activePanel != null && !(isMobile && props.activePanel === ToolpanelType.filter);
  return <>
      <AnimatePresence mode="wait">
        {showSlidePanel && <motion.div key={props.activePanel} className="absolute top-0 right-0 h-full bg-white border-l border-gray-200 shadow-lg z-(--z-header-popper) flex flex-col overflow-hidden" style={{
        width: sidebarWidth
      }} initial={SLIDE_ANIMATION.initial} animate={SLIDE_ANIMATION.animate} exit={SLIDE_ANIMATION.exit} transition={SLIDE_ANIMATION.transition} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <span className="text-sm font-semibold">{panelTitle}</span>
              <IconButton size="small" icon="navigation/close" onClick={props.onClose} />
            </div>
            <div className="flex-1 overflow-hidden">{panelContent}</div>
          </motion.div>}
      </AnimatePresence>

      {/* Mobile filter renders as a portal — independent of the slide panel */}
      {!props.filterComponent && <MobileFilterPanel isOpen={isMobile && props.activePanel === ToolpanelType.filter} onClose={props.onClose} columnConfig={props.columnConfig} filterState={props.filterState} onFilterChange={props.onFilterChange} onResetFilters={props.onResetFilters} />}
    </>;
}